import { action, observable, runInAction, toJS } from 'mobx';
// import { Policy } from '../../orders/api/policyApi';
import usersController from './controller';
import {
  PartnerLegalUserInterface, FirstUnpaidActInterface, ServiceFee, CurrentBalance, BalanceHistoryItem,
  PersonalStatisticReportRequest, PersonalStatisticItem, StreamBalance, UserStateItemInterface,
  UpdateUserInterface,
  MaxChannelItemIntrerface,
  ChangeUserPasswordRequestInterface,
} from './userApi';

export interface UserStore {
  partnerLegalUsers: PartnerLegalUserInterface[],
  partnerLegalUsersByUserId: string,
  partnerLegalUsersLoading: boolean,
  currentPartnerLegalUser: any,
  error: string,

  // все пользователи партнёра (или все пользователи без partnerId) — UserSelector (источник
  // partnerId) и CarmenUsers; бывш. Redux users.listOfAll. Ключ = partnerId или 'all'.
  usersByPartnerId: Record<string, PartnerUserItemInterface[]>,

  // взаиморасчёты
  balanceByUSerId: string,
  firstUnpaidAct: FirstUnpaidActInterface,
  serviceFees: ServiceFee[],
  currentBalance: CurrentBalance,
  balanceHistoryByUserId: string,
  balanceHistory: BalanceHistoryItem[],

  personalStatisticReport:PersonalStatisticItem[],

  streamBalance: StreamBalance,
  maxChannels: MaxChannelItemIntrerface[],

  userStates: UserStateItemInterface[],
  currentUserState: UserStateItemInterface,
}

/** Пользователь партнёра (GET /v1/users?partnerId=): для UserSelector важны id/fullName. */
export interface PartnerUserItemInterface {
  id: string,
  fullName?: string,
}

const initialValues: UserStore = {
  partnerLegalUsers: [],
  partnerLegalUsersByUserId: null,
  partnerLegalUsersLoading: false,
  currentPartnerLegalUser: null,
  error: null,

  usersByPartnerId: {},

  balanceByUSerId: null,
  firstUnpaidAct: null,
  serviceFees: [],
  currentBalance: null,
  balanceHistoryByUserId: null,
  balanceHistory: [],

  personalStatisticReport: [],

  streamBalance: null,
  maxChannels: [],

  userStates: [],
  currentUserState: null,
};

export const userStore = observable(initialValues);

// запросы в полёте (не обзерваблы: на рендер не влияют) — single-flight на дублирующие
// mount'ы с piggyback: API возвращает данные, повторный вызов садится на тот же промис
const usersByPartnerInFlight = new Map<string, Promise<PartnerUserItemInterface[]>>();

export const userActions = {
  setPolicies: action((partnerLegalUsers: PartnerLegalUserInterface[]) => {
    userStore.partnerLegalUsers = partnerLegalUsers;
  }),

  setCurrentPartnerLegalUser: action((currentPartnerLegalUser: any) => {
    userStore.currentPartnerLegalUser = currentPartnerLegalUser;
  }),

  clearCurrentPartnerLegalUser: action(() => {
    userStore.currentPartnerLegalUser = null;
    userStore.error = null;
  }),

  getPartnerLegalUsers: async (userId: string) => {
    try {
      userStore.partnerLegalUsersByUserId = userId;
      userStore.partnerLegalUsersLoading = true;
      const response = await usersController.getPartnerLegalUser(userId);
      userStore.partnerLegalUsers = response.data;
    } catch (err) {
      userStore.error = err.message || err;
      userStore.partnerLegalUsersByUserId = null;
    } finally {
      userStore.partnerLegalUsersLoading = false;
    }
  },

  createPartnerLegalUser: async (request: PartnerLegalUserInterface): Promise<void> => {
    const response = await usersController.createPartnerLegalUser(request);
    userActions.handleError(response);
  },

  deletePartnerLegalUser: async (id: string): Promise<void> => {
    const response = await usersController.deletePartnerLegalUser(id);
    userActions.handleError(response);
  },

  clearError: action(() => {
    userStore.error = null;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      userStore.error = errorData;
      throw errorData;
    }
  }),

  /*   setVehicleOwnersShowDeletedClients: action(() => {
    // eslint-disable-next-line no-param-reassign
    vehicleStore.vehicleOwnersShowDeletedClients = !vehicleStore.vehicleOwnersShowDeletedClients;
  }),

  setVehicleOwnersShowDeletedRelations: action(() => {
    // eslint-disable-next-line no-param-reassign
    vehicleStore.vehicleOwnersShowDeletedRelations = !vehicleStore.vehicleOwnersShowDeletedRelations;
  }), */

  clearPartnerLegalUsers: async () => {
    userStore.partnerLegalUsers = [];
    userStore.partnerLegalUsersByUserId = null;
    userStore.partnerLegalUsersLoading = false;
    userStore.currentPartnerLegalUser = null;
    userStore.balanceHistoryByUserId = null;
    userStore.balanceHistory = [];
    userStore.error = null;
  },

  clearFinanceData: async () => {
    userStore.firstUnpaidAct = null;
    userStore.serviceFees = [];
    userStore.currentBalance = null;
  },

  getFirstUnpaidAct: async (userId: string) => {
    try {
      userStore.balanceByUSerId = userId;
      const response = await usersController.getFirstUnpaidAct(userId);
      userStore.firstUnpaidAct = response.data;
    } catch (err) {
      userStore.error = err.message || err;
      userStore.balanceByUSerId = null;
    }
  },

  getServiceFees: async (userId: string) => {
    try {
      const response = await usersController.getServiceFees(userId);
      userStore.serviceFees = response.data;
    } catch (err) {
      userStore.error = err.message || err;
    }
  },

  getCurrentBalance: async (userId: string) => {
    try {
      const response = await usersController.getCurrentBalance(userId);
      userStore.currentBalance = response.data;
    } catch (err) {
      userStore.error = err.message || err;
    }
  },

  getBalanceHistory: async (userId: string) => {
    try {
      userStore.balanceHistoryByUserId = userId;
      const response = await usersController.getBalanceHistory(userId);
      userStore.balanceHistory = response.data;
    } catch (err) {
      userStore.balanceHistoryByUserId = null;
      userStore.error = err.message || err;
    }
  },

  getPersonalStatisticReport: async (request: PersonalStatisticReportRequest) => {
    const response = await usersController.getPersonalStatisticReport(request);
    userActions.handleError(response);
    if (!response.errorData) {
      userStore.personalStatisticReport = response.data.items;
    } else {
      userStore.personalStatisticReport = [];
    }
  },

  getStreamBalance: async () => {
    const response = await usersController.getStreamBalance();
    userActions.handleError(response);
    if (!response.errorData) {
      userStore.streamBalance = response.data;
    } else {
      userStore.streamBalance = null;
    }
  },

  getMaxChannels: async () => {
    const response = await usersController.getMaxChannels();
    userActions.handleError(response);
    if (!response.errorData) {
      userStore.maxChannels = response?.data?.chats;
    } else {
      userStore.maxChannels = [];
    }
  },

  clearMaxChannels: async () => {
    userStore.maxChannels = [];    
  },

  getUserStates: async () => {
    try {
      const response = await usersController.getUserStates();
      userStore.userStates = response?.data?.items ?? [];
    } catch (err) {
      userStore.error = err.message || err;
    }
  },

  setCurrentUserState: action((newState: UserStateItemInterface ) => {
    userStore.currentUserState = newState;
  }),

  clearCurrentUserState: action(() => {
    userStore.currentUserState = null;
  }),

  updateUser: async (request: UpdateUserInterface): Promise<void> => {
    userActions.clearError();
    const response = await usersController.updateUser(request);
    userActions.handleError(response);
  },

  changeUserPassword: async (request: ChangeUserPasswordRequestInterface): Promise<void> => {
    userActions.clearError();
    const response = await usersController.changeUserPassword(request);
    userActions.handleError(response);
  },

  // GET /v1/users?partnerId= (или /v1/users без параметра — все пользователи).
  // Бывш. Redux-танк getUsers (users.listOfAll): сортировка по fullName без учёта регистра,
  // как в исходном экшене. Вечный кэш на сессию по partnerId ('all' — запрос без параметра;
  // в Redux список перезапрашивался на каждый mount селектора), force нет — состав
  // пользователей меняется через страницы модуля users, которые сюда не пишут.
  // Возвращает toJS-копию (кэш стора потребители мутировать не могут); при ошибке — []
  // без записи в кэш, следующий mount повторит запрос.
  async getUsers(partnerId?: string): Promise<PartnerUserItemInterface[]> {
    const key = partnerId || 'all';
    if (Object.prototype.hasOwnProperty.call(userStore.usersByPartnerId, key)) {
      return toJS(userStore.usersByPartnerId[key]);
    }
    const inFlight = usersByPartnerInFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = (async () => {
      try {
        const response = await usersController.getUsers(partnerId);

        userActions.handleError(response);
        const users = [...(response?.data ?? [])].sort((a: PartnerUserItemInterface, b: PartnerUserItemInterface) => (
          (a.fullName ?? '').toUpperCase() > (b.fullName ?? '').toUpperCase() ? 1 : -1
        ));
        runInAction(() => {
          userStore.usersByPartnerId[key] = users;
        });
        return toJS(users);
      } catch {
        return [];
      } finally {
        usersByPartnerInFlight.delete(key);
      }
    })();

    usersByPartnerInFlight.set(key, promise);
    return promise;
  },

};
